// MapLibre image-atlas icons for ADS-B traffic.
//
// One airplane silhouette, six color variants tied to relative
// altitude vs ownship. Same SVG shape, different fill color. The
// layer picks the right variant per-target via a MapLibre `match`
// expression on a `colorClass` property we compute at push time
// (see TrafficLayer).
//
// Color palette mirrors ForeFlight + Garmin Pilot:
//   - red       : above ownship by > 500 ft   (descending threats)
//   - orange    : above ownship by 200-500 ft (closing)
//   - white     : co-altitude (within ±200 ft, highest visual priority)
//   - cyan      : below ownship by 200-500 ft (climbing)
//   - blue      : below ownship by > 500 ft   (slowest threats)
//   - amber     : emergency target — overrides all altitude colors

import { ensureSvgImages } from './iconRegistry';
import type { Map as MlMap } from 'maplibre-gl';

const VARIANTS = [
  { id: 'traffic-above',      fill: '#ef4444' },
  { id: 'traffic-near-above', fill: '#f97316' },
  { id: 'traffic-coalt',      fill: '#ffffff' },
  { id: 'traffic-near-below', fill: '#38bdf8' },
  { id: 'traffic-below',      fill: '#3b82f6' },
  { id: 'traffic-emergency',  fill: '#fbbf24' },
];

/**
 * Airplane silhouette pointing UP (heading 0). The layer rotates it
 * via `icon-rotate: ['get', 'track']`. 32 × 32 viewBox with a tight
 * tail+wing shape — reads at 16-24 device pixels without losing the
 * "this is an aircraft" silhouette.
 */
function planeSvg(fill: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
    <g stroke="#0b0d0f" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round">
      <path fill="${fill}" d="
        M 16 2
        L 18 12
        L 30 18
        L 30 21
        L 18 18.5
        L 18 25
        L 22 27
        L 22 29
        L 16 27.5
        L 10 29
        L 10 27
        L 14 25
        L 14 18.5
        L 2 21
        L 2 18
        L 14 12
        Z" />
    </g>
  </svg>`;
}

export async function ensureTrafficIcons(map: MlMap): Promise<void> {
  await ensureSvgImages(
    map,
    VARIANTS.map((v) => ({ id: v.id, svg: planeSvg(v.fill) })),
    { defaultSize: 32, logTag: '[trafficIcons]' },
  );
}

/** Public list of icon ids the layer-style expression switches on. */
export const TRAFFIC_ICON_IDS = VARIANTS.map((v) => v.id);
